// Récapitulatif avant « Préparer la facture » : ce qui sera créé, et ce qui bloquera la finalisation.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/Button';
import { formatEuros } from '../../services/billing/billingService';
import type { DossierExpense } from '../../services/billing/expenseService';
import { prepareInvoiceFromExpenses } from '../../services/billing/expenseBilling';
import { useDossier } from '../../hooks/queries/useDossiers';
import { expenseKeys } from '../../hooks/queries/useExpenses';
import { billingKeys } from '../../hooks/queries/useBillingDocuments';
import type { PreparationPlan } from '../../lib/billing/preparation';

type OkPlan = Extract<PreparationPlan<DossierExpense>, { ok: true }>;

const sum = (list: DossierExpense[], key: 'amount_ht' | 'amount_ttc') =>
  list.reduce((s, e) => s + Math.round(Number(e[key]) * 100), 0) / 100;

export function PrepareInvoiceDialog({ plan, onClose }: { plan: OkPlan | null; onClose: (done: boolean) => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: dossier } = useDossier(plan?.dossierId);
  const [busy, setBusy] = useState(false);
  const mandateMissing = plan && plan.debours.length > 0 && !(dossier?.debours_mandate_on && dossier?.debours_mandate_ref);

  async function run() {
    if (!plan) return;
    setBusy(true);
    try {
      const label = dossier ? `${dossier.number} ${dossier.title}` : 'dossier';
      const result = await prepareInvoiceFromExpenses(plan, label);
      await Promise.all([
        qc.invalidateQueries({ queryKey: expenseKeys.all }),
        qc.invalidateQueries({ queryKey: billingKeys.all }),
      ]);
      const made = [
        result.invoiceId ? 'brouillon de facture des frais' : null,
        result.disbursementId ? 'brouillon de facture de débours dans Polaris' : null,
      ].filter(Boolean).join(' et ');
      toast.success(`Préparé : ${made}.`, { description: result.warnings.join(' ') || undefined, duration: 8000 });
      onClose(true);
      if (result.invoiceId) navigate(`/facturation/${result.invoiceId}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Préparation impossible');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={!!plan} onOpenChange={(o) => { if (!o && !busy) onClose(false); }}>
      <DialogContent className="sm:max-w-md bg-white rounded-cime border-neutral-200">
        <DialogHeader>
          <DialogTitle className="text-cime-text-primary">Préparer la facture</DialogTitle>
          <DialogDescription className="text-cime-text-muted">
            {dossier ? `${dossier.number} — ${dossier.title}` : 'Dossier'} · rien n’est finalisé, les brouillons restent modifiables.
          </DialogDescription>
        </DialogHeader>
        {plan ? (
          <div className="flex flex-col gap-2.5 text-[13px]">
            {plan.frais.length > 0 ? (
              <div className="rounded-[10px] border border-[#e6e2d8] px-3.5 py-2.5">
                <b className="text-cime-text-primary">Frais</b> · {plan.frais.length} ligne{plan.frais.length > 1 ? 's' : ''} · {formatEuros(sum(plan.frais, 'amount_ht'))} HT
                <p className="mt-0.5 text-xs text-cime-text-muted">Brouillon de facture CIME, lignes de nature « Frais » avec TVA.</p>
              </div>
            ) : null}
            {plan.debours.length > 0 ? (
              <div className="rounded-[10px] border border-[#e6e2d8] px-3.5 py-2.5">
                <b className="text-cime-text-primary">Débours</b> · {plan.debours.length} ligne{plan.debours.length > 1 ? 's' : ''} · {formatEuros(sum(plan.debours, 'amount_ttc'))} sans TVA
                <p className="mt-0.5 text-xs text-cime-text-muted">Brouillon de facture de débours dans Polaris, justificatifs joints.</p>
              </div>
            ) : null}
            {mandateMissing ? (
              <p className="text-xs font-semibold text-cime-urgent">Mandat de débours non renseigné sur le dossier : Polaris refusera de finaliser.</p>
            ) : null}
            {plan.missingReceipts > 0 ? (
              <p className="text-xs font-semibold text-cime-urgent">
                {plan.missingReceipts} débours sans justificatif : Polaris refusera de finaliser tant qu’il manque.
              </p>
            ) : null}
          </div>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="ghost" disabled={busy} onClick={() => onClose(false)}>Annuler</Button>
          <Button type="button" variant="primary" isLoading={busy} onClick={run}>Préparer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
